import { HookForm, Switcher, type SwitcherOption } from "@fpsoftware/mvp";
import type BigNumber from "bignumber.js";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type Control,
  type FieldPath,
  useController,
  useForm,
} from "react-hook-form";
import type { FieldValues } from "react-hook-form/dist/types/fields";
import { formatMoney, toBigNumber } from "shared";

export interface Transformer {
  /** From base value to inner value **/
  from: (value: BigNumber | null) => BigNumber | null;
  /** From inner value to base value **/
  to: (value: BigNumber | null) => BigNumber | null;
}

export const Transformers = {
  Base: () => ({
    from: (x: BigNumber | null) => x,
    to: (x: BigNumber | null) => x,
  }),
  PercentageOf: (value: BigNumber | null) => ({
    from: (x: BigNumber | null) => {
      if (!x || !value) {
        return null;
      }

      if (value.eq(0)) {
        return null;
      }

      return x.div(value).multipliedBy(100);
    },
    to: (x: BigNumber | null) => {
      if (x === null || value === null) {
        return null;
      }

      return x.div(100).multipliedBy(value);
    },
  }),
  Fraction: (n: number) => ({
    from: (x: BigNumber | null) => x?.div(n) ?? null,
    to: (x: BigNumber | null) => x?.multipliedBy(n) ?? null,
  }),
} as const;

interface InputModes {
  [key: string]: {
    transformer: Transformer;
    label: string;
    dp: number;
    tooltip: string;
  };
}

function calcInnerValue(
  baseValue: BigNumber | null,
  baseDp: number,
  mode: InputModes[number],
) {
  // The conversion needs to be done twice so the correct DP is applied to the inner value
  // When user has typed 1000 USD and then changes to monthly payment, the annual payment changes because
  // 1000 is not divisible by 12 (It's 83.333333...)
  // Example 1000 USD/year -> 83.33/month = 999.96/year
  const inner = mode.transformer.from(baseValue)?.dp(mode.dp) ?? null;
  const annual = mode.transformer.to(inner)?.dp(baseDp) ?? null;

  // Corrected value
  return mode.transformer.from(annual)?.dp(mode.dp) ?? null;
}

interface PriceInputProps<TFieldValues extends FieldValues>
  extends Omit<
    ComponentProps<typeof HookForm.NumberInput>,
    "control" | "name" | "suffix"
  > {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  modes: InputModes;
  defaultMode: string;
  defaultTyped?: number;
  /** Label shown before the computed base value summary. Defaults to "Annual". */
  summaryLabel?: string;
}

export function PriceInput<TFieldValues extends FieldValues>(
  props: PriceInputProps<TFieldValues>,
) {
  const {
    control,
    name,
    modes,
    defaultMode,
    defaultTyped,
    summaryLabel = "Annual",
    dp = 2,
    ...rest
  } = props;

  const [mode, setMode] = useState<string>(defaultMode);
  const currentMode = useMemo(() => {
    return modes[mode];
  }, [modes, mode]);

  const {
    field: { value: baseRaw, onChange: setBase },
  } = useController({ control, name });
  const baseValue = useMemo(() => toBigNumber(baseRaw), [baseRaw]);

  const {
    control: innerControl,
    watch,
    setValue,
  } = useForm<{ innerValue: string | null }>({
    defaultValues: {
      innerValue:
        currentMode.transformer.from(baseValue)?.dp(currentMode.dp).toFixed() ??
        defaultTyped?.toString() ??
        null,
    },
  });

  const innerRaw = watch("innerValue");
  const innerValue = useMemo(() => toBigNumber(innerRaw), [innerRaw]);

  // TODO: Follow when external changes (endless loop exists with state, needs to be thought thoroughly)

  // Internal changed
  useEffect(() => {
    const newBaseValue = currentMode.transformer.to(innerValue);
    setBase(newBaseValue);
  }, [innerValue, currentMode, setBase]);

  // Mode changed
  const handleModeChange = useCallback(
    (value: string) => {
      setMode(value);

      const newMode = modes[value];
      const innerValue = calcInnerValue(baseValue, dp, newMode);

      setValue("innerValue", innerValue?.toString() ?? null);
    },
    [baseValue, modes, dp, setValue],
  );

  const options = useMemo<SwitcherOption<string>[]>(
    () =>
      Object.entries(modes).map(([value, mode]) => ({
        value,
        label: mode.label,
        tooltip: mode.tooltip,
      })),
    [modes],
  );

  return (
    <>
      <HookForm.NumberInput
        control={innerControl}
        name="innerValue"
        className="mb-1"
        dp={currentMode.dp}
        suffix={
          <Switcher
            value={mode}
            onChange={handleModeChange}
            options={options}
          />
        }
        {...rest}
      />
      <div>
        <small>
          {summaryLabel}: {formatMoney(baseValue)}
        </small>
      </div>
    </>
  );
}
